import {Template} from "../../../components/template/template.jsx";
import TemplateBody from "../../../components/template/template-body.jsx";
import {Card} from "../../../components/card/card.jsx";
import InputDateTime from "../../../components/input/input-datetime.jsx";
import React, {useContext, useEffect, useMemo, useState} from "react";
import InputForm from "../../../components/input/input-form.jsx";
import InputSlider from "../../../components/input/input-slider.jsx";
import Button from "../../../components/button/button.jsx";
import {useQuery} from "@tanstack/react-query";
import corporationService from "../../../services/web/corporation-service.js";
import {useCapturePlacesTree} from "../../../components/tree-lanes/hooks/useCapturePlacesTree.js";
import {FaChevronLeft, FaChevronRight, FaEraser, FaSearch,} from "react-icons/fa";
import TreeLanesInput from "../../../components/tree-lanes/tree-lanes-input.jsx";
import {addHours, differenceInDays, differenceInHours, differenceInMinutes, format, isBefore, subHours} from "date-fns";
import GeoreferencedSearchService from "../../../services/web/georeferenced-search-service.js";
import georeferencedSearchService from "../../../services/web/georeferenced-search-service.js";
import {useSearchParams} from "react-router";
import GeoreferencedSearchTable from "./components/georeferenced-search-table.jsx";
import {
    buildGlyphMap,
    getDefaultData,
    getMarkersData,
    groupDataByPlate,
    initialOptions,
    traceRoute
} from "./utils/georeferenced-search-utils.js";
import GeoreferencedSearchMap from "./components/map/georeferenced-search-map.jsx";
import GeoreferencedSearchPassageDetail from "./components/georeferenced-search-passage-detail.jsx";
import projectUtils from "../../../utils/project-utils.js";
import {GeoreferencedSearchContext} from "./context/georeferenced-search-context.jsx";
import GeoreferencedSearchCortexModal from "./components/georeferenced-search-cortex-modal.jsx";
import userService from "../../../services/web/user-service.js";
import Split from "react-split";
import ToggleButton from "./components/toggle-button.jsx";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import LatLngInput from "../../../components/input/lat-lng-input.jsx";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import {v4 as uuidv4} from "uuid";
import capturePlacesService from "../../../services/web/capture-places-service.js";

const MySwal = withReactContent(Swal);
const GeoreferencedSearchIndex = () => {
    const {data, setData, options, setOptions, selection, hiddenDetails, setHiddenDetails, colorRegistry, zIndexRegistry, zIndexCounter, registryVersion,
        activePlates, setActivePlates, orderedData, setSelection, mapZoomController, setAnimatedStatus} = useContext(GeoreferencedSearchContext);
    const [isLoading, setIsLoading] = useState(false);
    const [dtStart, setDtStart] = useState(new Date());
    const [dtEnd, setDtEnd] = useState(addHours(new Date(), 1));
    const [plate, setPlate] = useState("");
    const [radius, setRadius] = useState(10);
    const [markerPosition, setMarkerPosition] = useState(null);
    const [markerPositionString, setMarkerPositionString] = useState("");
    const [showCortexModal, setShowCortexModal] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [searchParams] = useSearchParams();
    const passages = useMemo(() => {
        return searchParams.get("passages") || new URLSearchParams(window.location.search).get("passages");
    }, [searchParams]);

    const glyphMap = useMemo(() => {
        return buildGlyphMap({
            data,
            orderedData,
            syncWithGrid: options.orderAlphabetically
        });
    }, [data, orderedData, options.orderAlphabetically]);


    const rowData = useMemo(
        () => {
            if(options.groupByPlate){
                return groupDataByPlate(data);
            }
            else {
                return getDefaultData(data);
            }
        },
        [data, options.groupByPlate, options.showTimeBetweenPassage, registryVersion]
    );

    const replicatedPlates = useMemo(() => {
        if (!options.groupByPlate) return new Set();
        return new Set(
            rowData
                .filter(row => row.qtd > 1)
                .map(row => row.plate)
        );
    }, [rowData, options.groupByPlate]);

    const markersData = useMemo(
        () => getMarkersData(data, colorRegistry, zIndexRegistry, options, replicatedPlates, glyphMap),
        [data, options.groupByPlate, replicatedPlates, registryVersion, glyphMap]
    );

    const activePassages = useMemo(() => {
        if (activePlates.size === 0) return [];

        return data.filter(p =>
            activePlates.has(p.plate.rawValue)
        );
    }, [activePlates, data]);

    const polylines = useMemo(() => {
        if (activePassages.length === 0) return [];

        return traceRoute(
            activePassages,
            colorRegistry,
            options,
            replicatedPlates,
            zIndexRegistry
        );
    }, [
        activePassages,
        options.groupByPlate,
        replicatedPlates,
        registryVersion,
        data
    ]);

    const [treeLaneCheckedStatus, setTreeLaneCheckedStatus] = useState({});
    const {
        nodes,
        checkedInitialState,
        isLoadingTree,
        inputCamerasValue

    } = useCapturePlacesTree(capturePlacesService, treeLaneCheckedStatus);

    useEffect(() => {
        if (!nodes.length || Object.keys(treeLaneCheckedStatus).length > 0) return;
        setTreeLaneCheckedStatus(checkedInitialState);
    }, [nodes, checkedInitialState, treeLaneCheckedStatus]);

    const {data: corporation, isSuccess: isSuccessCorporation} = useQuery({
        queryFn: () => corporationService.entity(),
        queryKey: ["corporation"]
    });

    const {data: currentUser, isSuccess: isSuccessUser} = useQuery({
        queryFn: () => userService.getUserLogged(),
        queryKey: ["currentUser"]
    });

    const {data: limitSearch, isSuccess: isSuccessLimit} = useQuery({
        queryFn: () => georeferencedSearchService.limitSearch(),
        queryKey: ["limitSearch"]
    });

    const latitude = parseFloat(corporation?.latitude) || 0;
    const longitude = parseFloat(corporation?.longitude) || 0;


    const position = {
        lat: latitude,
        lng: longitude
    }

    const getField = (field, value, type = "like") => ({ field, value, type });

    const apply = async () => {
        if(passages) return;
        setActivePlates(new Set())
        setSelection({
            type: 'none',
            data: null
        })
        setOptions(initialOptions);
        const isValid = validate();

        if(isValid){
            projectUtils.swalErrorMessage("", isValid, "Ok");
            return
        }

        let adjustDtEnd = null;
        let adjustDtStart = null;

        if(currentUser.permissions?.includes("sentry.passage.search.limit.time")){
            const limit = Number(limitSearch.value);
            const currentDate = new Date();
            if(differenceInHours(currentDate, dtStart) >= limit){
                adjustDtStart = subHours(currentDate, limit);
                adjustDtEnd = addHours(adjustDtStart, limit - 1);
                setDtStart(adjustDtStart)
                setDtEnd(adjustDtEnd)
                await MySwal.fire({
                    icon: 'error',
                    title: 'Atenção',
                    text: `O filtro de tempo foi ajustado em ${limit} horas, conforme a permissão concedida.`,
                    confirmButtonColor: '#063A6C',
                    confirmButtonText: 'OK'
                })
            }
        }

        setIsLoading(true);
        const dataToSend = {
            filter: [
                getField("dtStart", adjustDtStart ? format(adjustDtStart, "dd/MM/yyyy HH:mm") : format(dtStart, "dd/MM/yyyy HH:mm")),
                getField("dtEnd", adjustDtEnd ? format(adjustDtEnd, "dd/MM/yyyy HH:mm") : format(dtEnd, "dd/MM/yyyy HH:mm")),
                getField("plate", plate),
                getField("lanes", JSON.stringify(inputCamerasValue.values.map(el => String(el.code)))),
                getField("latitude", position.lat),
                getField("longitude", position.lng),
                getField("range", radius)
            ],
            page: 1,
            size: 50
        }

        try{
            const response = await GeoreferencedSearchService.list(dataToSend);
            setData(response)
            mapZoomController.current = 0
        }
        catch(error) {
            let message = 'Não foi possível realizar a busca.';
            if (error.response.status !== 500) {
                message = error;
            }

            projectUtils.swalErrorMessage('', message, 'Ok');
        }
        finally{
            setIsLoading(false)
        }
    }

    const changeOption = (option) => {
        if(option === "groupByPlate"){
            setSelection({
                type: 'none',
                data: null
            })
        }
        toggleOption(option);
    }

    function toggleOption(option) {
        setOptions(prev => {
            if(option === "groupByPlate"){
                setActivePlates(new Set());
                mapZoomController.current = 0
            }
            return {
                ...prev,
                [option]: !prev[option],
            };
        });
    }

    const allRoutesActive =
        replicatedPlates.size > 0 &&
        [...replicatedPlates].every(plate =>
            activePlates.has(plate)
        );

    useEffect(() => {
        if(!options.groupByPlate) return;

        zIndexCounter.current += 10;
        const baseZ = zIndexCounter.current;
        data.filter(el => replicatedPlates.has(el.plate.rawValue)).forEach(el => {
            zIndexRegistry.current.set(el.id, baseZ)
        })
    }, [options.groupByPlate])

    function validate(){
        if (!dtStart) return `O campo "Data inicial" é obrigatório.`;
        if (!dtEnd) return `O campo "Data final" é obrigatório.`;
        if (inputCamerasValue.values.length === 0) return `O campo "Câmeras" é obrigatório.`;
        if (!markerPosition) return `O campo "Ponto no mapa" é obrigatório.`;

        if (isBefore(dtEnd, dtStart)) {
            return "A data inicial não pode ser maior que a data final.";
        }

        if (plate !== "" && plate.includes(",")) {
            const split = plate.split(",");

            for (const element of split) {
                if (element.trim().length < 7) {
                    return "Para pesquisas com mais de uma placa, todas elas precisam ser exatas.";
                }
            }
        }

        const diffMinutes = differenceInMinutes(dtEnd, dtStart);
        const exceeds48Hours = diffMinutes > 2880;

        const diffDays = differenceInDays(dtEnd, dtStart);
        const exceeds30Days = diffDays > 30;

        if (plate === "" && exceeds48Hours) {
            return "Para pesquisar sem a placa do veículo, o período deve ser no máximo 48 horas.";
        }

        if (plate.length < 7 && exceeds30Days) {
            return "Para pesquisar utilizando uma placa incompleta, o período deve ser no máximo 30 dias.";
        }

        return "";
    }

    const clear = () => {
        if(passages) return;
        setOptions(initialOptions)
        setDtStart(new Date());
        setDtEnd(addHours(new Date(), 1));
        setPlate("");
        setRadius(10);
        setMarkerPosition(null);
        setMarkerPositionString("")
        setTreeLaneCheckedStatus(checkedInitialState)
        setData([]);
        setSelection({
            type: 'none',
            data: null
        })
    }

    useEffect(() => {
        if(!passages) return;
        const fetchData = async () => {
            setIsLoading(true)
            try {
                const base64 = decodeURIComponent(passages);
                const dataToSend = {filter: [{'field': "passage", 'type': "like", 'value': base64}], page: 1, size: 50}
                try{
                    const response = await GeoreferencedSearchService.listSentryView(dataToSend);
                    setData(response.map(el => ({...el, id: uuidv4()})))
                }
                catch(error) {
                    let message = 'Ocorreu um erro na requisição de dados do indivíduo.';
                    if (error.response.status !== 500) {
                        message = error;
                    }

                    projectUtils.swalErrorMessage('', message, 'Ok');
                }
                finally{
                    setIsLoading(false)
                }

            } catch (e) {
                console.error("Erro ao decodificar passagens", e);
            }
        }
        fetchData()
    }, [passages]);

    useGlobalKey("Enter", () => {
        if (!showModal) apply()
    });
    useGlobalKey("Escape", () => {
        if (!showModal) clear()
    });

    return(
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>
                    Pesquisa de passagens - Mapa
                </Template.Title>
            </Template.Header>
            <TemplateBody>
                <div className="flex flex-col gap-2">
                    <Card>
                        <Card.Body className="flex items-center w-full">
                            <div className="flex flex-1 gap-6 items-center">
                                <InputDateTime
                                    classNameWrapper="flex-1 min-w-[160px]"
                                    id="dtStart"
                                    selected={dtStart ? dtStart : undefined}
                                    onChange={(date) => setDtStart(date)}
                                    label={"Data inicial *"}
                                    dateFormat="LL"
                                    disabled={!!passages}
                                />
                                <InputDateTime
                                    classNameWrapper="flex-1 min-w-[160px]"
                                    id="dtEnd"
                                    selected={dtEnd ? dtEnd : undefined}
                                    onChange={(date) => setDtEnd(date)}
                                    label={"Data final *"}
                                    dateFormat="LL"
                                    disabled={!!passages}
                                />
                                <TreeLanesInput treeLaneCheckedStatus={treeLaneCheckedStatus}
                                                setTreeLaneCheckedStatus={setTreeLaneCheckedStatus}
                                                inputCamerasValue={inputCamerasValue.label}
                                                showModal={showModal} setShowModal={setShowModal}
                                                nodes={nodes}
                                                disabled={!!passages}
                                                className="flex-4 cursor-pointer min-w-[140px]"/>
                                {isSuccessCorporation && <LatLngInput label="Ponto no mapa *"
                                                                      position={position}
                                                                      corporation={corporation}
                                                                      setMarkerPositionString={setMarkerPositionString}
                                                                      markerPositionString={markerPositionString}
                                                                      markerPosition={markerPosition}
                                                                      setMarkerPosition={setMarkerPosition}
                                                                      disabled={!!passages}
                                                                      className={"flex-1 cursor-pointer"}
                                />}
                                <InputSlider label="Raio (M):" value={radius} setValue={setRadius} min={10} max={10000} className={"max-w-60"}/>
                                <InputForm id="plateInput" disabled={!!passages} classNameWrapper="flex-1" inputClass="font-bold min-w-[90px]" label="Placa" value={plate} onChange={({target}) => setPlate(target.value.toUpperCase())}/>
                            </div>
                            <div className="flex gap-2 ml-6">
                                <Button disabled={!!passages} target="primary-outline" onClick={clear}><FaEraser/></Button>
                                <Button disabled={!!passages} onClick={apply}><FaSearch/></Button>
                            </div>
                        </Card.Body>
                    </Card>
                    <Card >
                        <Card.Body>
                            <div className="flex gap-1 h-full">
                                <Split  className="flex flex-1"
                                        sizes={[40, 60]}
                                        minSize={[100, 400]}
                                        gutterSize={30}
                                        direction="horizontal">
                                    <div className="flex flex-col gap-2 items-center">
                                        <div className="flex self-start p-1 gap-1 rounded-lg w-fit border-[1px] border-[#e5e7eb] ">
                                            <ToggleButton
                                                label="Agrupar por placa"
                                                disabled={
                                                    data.length === 0 ||
                                                    options.showTimeBetweenPassage ||
                                                    options.orderAlphabetically
                                                }
                                                title="Agrupar por placa"
                                                checked={options.groupByPlate}
                                                onChange={() => changeOption("groupByPlate")}
                                            />
                                            <ToggleButton
                                                label="Tempo entre passagens"
                                                disabled={
                                                    data.length === 0 ||
                                                    !data.every(p => p.plate?.rawValue === data[0].plate?.rawValue) ||
                                                    options.groupByPlate
                                                }
                                                title="Para habilitar esse recurso é necessário filtrar por uma única placa."
                                                checked={options.showTimeBetweenPassage}
                                                onChange={() => changeOption("showTimeBetweenPassage")}
                                            />
                                            <ToggleButton
                                                label="Ordem crescente"
                                                disabled={data.length === 0 || options.groupByPlate}
                                                checked={options.orderAlphabetically}
                                                onChange={() => changeOption("orderAlphabetically")}
                                                title="Listar em ordem crescente"
                                            />
                                        </div>
                                        <GeoreferencedSearchTable rowData={rowData}
                                                                  markersData={markersData}
                                                                  className="h-[calc(100vh-277px)] w-full"
                                                                  replicatedPlates={replicatedPlates}
                                        />
                                        <Button className="w-[240px]" onClick={() => setShowCortexModal(true)}>Adicionar passagens do Córtex</Button>
                                    </div>
                                    <div className="relative flex flex-col">
                                        <div className="flex items-center p-1 gap-1 rounded-lg w-fit border-[1px] border-[#e5e7eb] mb-2">
                                            <ToggleButton
                                                label="Traçar rota"
                                                disabled={data.length === 0}
                                                checked={allRoutesActive}
                                                title="Traçar rota por placa"
                                                onChange={() => {
                                                    if (allRoutesActive) {
                                                        setActivePlates(new Set());
                                                        return;
                                                    }
                                                    setOptions(prev => ({ ...prev, groupByPlate: true }));
                                                    const plateCount = data.reduce((acc, el) => {
                                                        const plate = el.plate.rawValue;
                                                        acc[plate] = (acc[plate] || 0) + 1;
                                                        return acc;
                                                    }, {});

                                                    const plateSet = new Set(
                                                        Object.entries(plateCount)
                                                            .filter(([_, count]) => count > 1)
                                                            .map(([plate]) => plate)
                                                    );
                                                    setActivePlates(plateSet);
                                                }}

                                            />
                                            <ToggleButton
                                                label="Agrupar itens"
                                                disabled={data.length === 0}
                                                checked={options.groupByMarker}
                                                onChange={() => changeOption("groupByMarker")}
                                                title="Agrupar itens"
                                            />
                                        </div>
                                        {data.length === 0
                                            ? <div className="bg-[#ebebeb] w-full h-full flex justify-center items-center text-[15px] text-[#676a6c]">
                                                <span className="font-bold">Sem informações para exibição.</span>
                                            </div>
                                            : <GeoreferencedSearchMap corporation={corporation}
                                                                      position={position}
                                                                      markersData={markersData}
                                                                      polylines={polylines}
                                                                      replicatedPlates={replicatedPlates}
                                            />
                                        }
                                        {selection?.type === 'single' && (
                                            <button
                                                onClick={() => setHiddenDetails(prev => !prev)}
                                                className="absolute -right-3 top-1/4 z-50 bg-white p-1 border shadow"
                                            >
                                                {hiddenDetails ?
                                                    <FaChevronLeft size={14} color="#000"/> :
                                                    <FaChevronRight size={14} color="#000"/>
                                                }
                                            </button>
                                        )}
                                    </div>
                                </Split>
                                {selection?.type === 'single' && !hiddenDetails && (
                                    <GeoreferencedSearchPassageDetail
                                        className="w-[25%] mt-[48px]"
                                        replicatedPlates={replicatedPlates}
                                    />
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                </div>
                {isSuccessUser &&
                    <GeoreferencedSearchCortexModal open={showCortexModal} setOpen={setShowCortexModal} setData={setData} user={currentUser} setIsLoading={setIsLoading}/>}
            </TemplateBody>
        </Template>
        )
}

export default GeoreferencedSearchIndex;